import type { FilterOption } from '../types';

/**
 * The people a "Project manager" filter may name.
 *
 * Built from the projects list's own `managers[]`, which the API assembles from
 * `projectMember.findMany({ isManager: true, removedAt: null })` — the SAME
 * membership the report predicate tests (`report-builder.sql.ts`, the EXISTS
 * clause) and the same one the AI drafter's catalog enumerates. That agreement
 * is the whole point of this file.
 *
 * The alternative in the registry — `managers`, i.e. `useEmployees({ status:
 * 'active' })` — offers every active employee. On the live database that is 47
 * people of whom 10 hold a manager membership, so 37 of the offered names run a
 * report that returns zero hours with nothing on screen to say the question was
 * unanswerable. Worse, the drafter refuses those names as unresolvable, so the
 * two halves of one feature disagreed about what a project manager is.
 *
 * `Project.managerId` is deliberately not read here: the schema calls it a
 * legacy display-only pointer, and PeopleForce sync writes it without ever
 * setting `isManager`.
 */

interface ProjectsPage {
  data?: Array<{
    managers?: Array<{
      id: string;
      firstName: string;
      lastName: string;
      avatarUrl?: string;
    }> | null;
  }>;
}

export function projectManagerOptions(
  data: unknown,
  formatName?: (person: { firstName: string; lastName: string }) => string,
): FilterOption[] {
  const page = data as ProjectsPage | undefined;
  if (!page?.data) return [];

  const byId = new Map<string, { option: FilterOption; projects: number }>();
  for (const project of page.data) {
    for (const manager of project.managers ?? []) {
      const found = byId.get(manager.id);
      if (found) {
        found.projects += 1;
        continue;
      }
      byId.set(manager.id, {
        option: {
          value: manager.id,
          label: formatName
            ? formatName(manager)
            : `${manager.firstName} ${manager.lastName}`.trim(),
          avatar: manager.avatarUrl,
        },
        projects: 1,
      });
    }
  }

  return [...byId.values()]
    .map(({ option, projects }) => ({
      ...option,
      // Says why this person is on the list at all, and lets a reader tell a
      // manager of one project from the one who runs half the portfolio.
      description: projects === 1 ? '1 project' : `${projects} projects`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
